/**
 * FP3-078 — Student-Data Authorization Acknowledgment modal.
 *
 * One-time authorization checkpoint shown BEFORE the user can
 * create or import student data. NOT a paid customer agreement,
 * NOT a Stripe / billing / subscription touchpoint — purely a
 * professional confirmation that the operator has the appropriate
 * authorization to enter student information.
 *
 * Driven by the auth context's `studentDataAckPending` promise
 * slot: call sites use `await ensureStudentDataAck()` and the
 * gate resolves either immediately (already acknowledged) or
 * after the user submits this modal.
 *
 * Wording is calm and professional. No red banners, no scary
 * legal language. The full Terms of Use stays one click away.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function StudentDataAckModal() {
  const {
    studentDataAckPending,
    studentDataAckStatus,
    completeStudentDataAck,
    refreshStudentDataAck,
    openStudentDataAck,
  } = useAuth();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // FP3-078B — Listen for the global axios interceptor's open event.
  // When ANY API call returns 403 needs_student_data_ack (e.g. the
  // FE missed wrapping a call site, or the auth context hadn't
  // hydrated yet), the interceptor dispatches `student-data-ack:open`
  // and waits for `student-data-ack:resolved`. This handler hooks
  // the modal into that contract so the request auto-retries after
  // confirmation.
  useEffect(() => {
    const onOpen = () => {
      if (openStudentDataAck) openStudentDataAck();
    };
    window.addEventListener("student-data-ack:open", onOpen);
    return () => window.removeEventListener("student-data-ack:open", onOpen);
  }, [openStudentDataAck]);

  // Show even when status hasn't loaded yet — the FP3-078B
  // interceptor uses the global event, which means we may pop
  // before /auth/me has hydrated `studentDataAckStatus`. We refresh
  // it lazily here so the modal can render with full wording.
  useEffect(() => {
    if (studentDataAckPending && !studentDataAckStatus && refreshStudentDataAck) {
      refreshStudentDataAck();
    }
  }, [studentDataAckPending, studentDataAckStatus, refreshStudentDataAck]);

  if (!studentDataAckPending) return null;
  if (studentDataAckStatus && studentDataAckStatus.acknowledged) return null;

  // Use sensible defaults if status hasn't loaded yet so the
  // modal still renders calm copy instead of flashing empty.
  const requiredVersion =
    studentDataAckStatus?.required_version ||
    "2026-06-frame-practice-student-data-ack-v1";
  const requiredTitle =
    studentDataAckStatus?.required_title || "Student Data Authorization";
  const intro =
    studentDataAckStatus?.intro ||
    "Before you add or import student information, Frame Practice needs " +
      "you to confirm that you have the appropriate authorization to do so.";
  const checkboxLabel =
    studentDataAckStatus?.checkbox_label ||
    "I confirm that I am authorized to enter student information into " +
      "Frame Practice for educational or service-delivery purposes.";
  const footerNote =
    studentDataAckStatus?.footer_note ||
    "This authorization is recorded against your account. You can review " +
      "the full Frame Practice Terms of Use any time from the Terms page.";

  const dispatchResolved = (success) => {
    try {
      window.dispatchEvent(
        new CustomEvent("student-data-ack:resolved", { detail: { success } })
      );
    } catch {
      /* noop — old browsers without CustomEvent will still resolve via
         the auth-context promise gate */
    }
  };

  const handleCancel = () => {
    setChecked(false);
    completeStudentDataAck(false);
    dispatchResolved(false);
  };

  const handleSubmit = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await api.acknowledgeStudentData({
        student_data_ack_version: requiredVersion,
        confirmation: true,
      });
      toast.success("Thanks — you're all set.");
      await completeStudentDataAck(true);
      dispatchResolved(true);
      setChecked(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not save the acknowledgment.";
      toast.error(typeof detail === "string" ? detail : "Could not save the acknowledgment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="student-data-ack-modal"
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-data-ack-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#E5E3DB] bg-[#FBFAF6] flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E8F0EB] text-[#1E3F33] flex items-center justify-center shrink-0">
            <ShieldCheck size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="student-data-ack-title"
              data-testid="student-data-ack-title"
              className="text-[15px] font-semibold text-[#1F2937] heading-font"
            >
              {requiredTitle}
            </h2>
            <p className="text-[12px] text-[#6B7280] mt-0.5">
              One-time checkpoint · Not a contract
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            data-testid="student-data-ack-close"
            aria-label="Close"
            className="p-1 rounded-md hover:bg-[#F3F1EC] text-[#6B7280]"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          <p className="text-[12.5px] text-[#374151] leading-relaxed">
            {intro}
          </p>
          <label
            data-testid="student-data-ack-checkbox-label"
            className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-[#E5E3DB] bg-[#FBFAF6] px-3 py-3 hover:bg-[#F3F1EC] transition-colors"
          >
            <Checkbox
              data-testid="student-data-ack-checkbox"
              checked={checked}
              onCheckedChange={(c) => setChecked(!!c)}
              className="mt-0.5"
            />
            <span className="text-[12.5px] text-[#1F2937] leading-relaxed">
              {checkboxLabel}
            </span>
          </label>
          <p className="text-[11px] text-[#6B7280]">
            {footerNote}{" "}
            <Link
              to="/terms"
              data-testid="student-data-ack-terms-link"
              className="underline decoration-[#8FA998]/60 underline-offset-2 hover:text-[#1E3F33]"
            >
              Open Terms of Use
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E3DB] bg-[#FBFAF6] flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            data-testid="student-data-ack-cancel"
            className="h-9 px-3 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!checked || submitting}
            data-testid="student-data-ack-submit"
            className="h-9 px-4 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] text-white disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Confirm and continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
